/**
 * Manager Loyalty — Redemption Terminal (Phase 1, Part-B Stabilization).
 *
 * Two independent redemption modes:
 *   1. Voucher — enter voucher_code, validate, confirm.
 *   2. Membership Card 20% (EDC) — enter card_number OR mobile, validate,
 *      enter bill amount, confirm. 20% discount is auto-applied.
 *
 * Design:
 *   • POS-lite look, flush-left alignment inside content card.
 *   • Equal-width inputs, consistent gap, large touch targets (min-h-12).
 *   • RestroPulse tokens (primary var(--color-primary), secondary #C9A227).
 *   • Modal-free flow so mobile scrolling is never blocked.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Gift, ScanLine, RefreshCw, CheckCircle2, XCircle,
  User, CreditCard, Percent, Phone, Camera,
} from 'lucide-react';
import VoucherQrScanner from '../components/loyalty/VoucherQrScanner';

const INR = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const PRIMARY = 'var(--color-primary)';
const SECONDARY = '#C9A227';

export default function ManagerLoyalty() {
  const [tab, setTab] = useState('voucher');
  const [recent, setRecent] = useState([]);
  const [edcRecent, setEdcRecent] = useState([]);
  const { getHeaders, API, user } = useAuth();

  const loadRecent = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/manager/loyalty/redemptions/recent`, { headers: getHeaders() }),
        axios.get(`${API}/manager/loyalty/edc/recent`, { headers: getHeaders() }),
      ]);
      setRecent(r1.data || []);
      setEdcRecent(r2.data || []);
    } catch { /* ignore */ }
  }, [API, getHeaders]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="manager-loyalty-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ color: PRIMARY }}>
          <Gift className="w-6 h-6" style={{ color: SECONDARY }} />
          Loyalty Redemption
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Choose a redemption mode below. One transaction per redemption.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full sm:w-auto">
              <TabsTrigger value="voucher" data-testid="tab-voucher">
                <ScanLine className="w-4 h-4 mr-2" />Voucher
              </TabsTrigger>
              <TabsTrigger value="edc" data-testid="tab-edc">
                <CreditCard className="w-4 h-4 mr-2" />Membership Card 20%
              </TabsTrigger>
            </TabsList>

            <TabsContent value="voucher" className="mt-4">
              <VoucherRedemptionPanel
                API={API} headers={getHeaders} outletId={user?.outlet_id}
                onDone={loadRecent}
              />
            </TabsContent>

            <TabsContent value="edc" className="mt-4">
              <EdcRedemptionPanel
                API={API} headers={getHeaders} outletId={user?.outlet_id}
                onDone={loadRecent}
              />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <RecentList title="Recent Vouchers" testid="recent-vouchers" rows={recent}
                      onRefresh={loadRecent}
                      empty="No voucher redemptions yet." />
          <RecentList title="Recent Cards (20%)" testid="recent-edc" rows={edcRecent}
                      onRefresh={loadRecent}
                      empty="No card redemptions yet." isEdc />
        </div>
      </div>
    </div>
  );
}

/* ==================== VOUCHER PANEL ==================== */

function VoucherRedemptionPanel({ API, headers, outletId, onDone }) {
  const [code, setCode] = useState('');
  const [validation, setValidation] = useState(null);
  const [bill, setBill] = useState({ bill_ref: '', bill_amount: '', discount_amount: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const scanRef = useRef(null);
  const submitLock = useRef(false);  // F-3 · synchronous double-submit guard

  const validate = async (codeOverride) => {
    const useCode = (codeOverride ?? code).trim();
    if (!useCode) return;
    setBusy(true); setValidation(null);
    try {
      const res = await axios.post(`${API}/manager/loyalty/redeem/validate`, {
        voucher_code: useCode,
        outlet_id: outletId || undefined,
      }, { headers: headers() });
      setValidation({ ok: true, ...res.data });
    } catch (e) {
      setValidation({ ok: false, detail: e?.response?.data?.detail || 'Validation failed' });
    } finally { setBusy(false); }
  };

  const handleScan = (scannedCode) => {
    setScannerOpen(false);
    setCode(scannedCode);
    toast.success(`Scanned ${scannedCode}`);
    // Auto-validate — bill capture stays as the confirm step.
    validate(scannedCode);
  };

  const redeem = async () => {
    if (!validation?.ok) return;
    // F-3 — synchronous ref lock beats the React state re-render race.
    // Rapid taps that arrive before setBusy(true) flushes are dropped here.
    if (submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    try {
      await axios.post(`${API}/manager/loyalty/redeem`, {
        voucher_code: validation.voucher.voucher_code,
        outlet_id: outletId || undefined,
        bill_ref: bill.bill_ref || undefined,
        bill_amount: bill.bill_amount === '' ? null : Number(bill.bill_amount),
        discount_amount: bill.discount_amount === '' ? null : Number(bill.discount_amount),
        notes: bill.notes || undefined,
      }, { headers: headers() });
      toast.success('Voucher redeemed');
      reset();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Redeem failed');
    } finally {
      submitLock.current = false;
      setBusy(false);
    }
  };

  const reset = () => {
    setCode(''); setValidation(null);
    setBill({ bill_ref: '', bill_amount: '', discount_amount: '', notes: '' });
    scanRef.current?.focus();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ color: PRIMARY }}>
          <ScanLine className="w-4 h-4" />Scan / Enter Voucher Code
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Voucher Code</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              ref={scanRef}
              placeholder="e.g. AB12CD34"
              className="min-h-12 text-lg tracking-wider uppercase flex-1"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && validate()}
              autoFocus
              data-testid="voucher-code-input"
            />
            <Button
              onClick={() => setScannerOpen(true)}
              variant="outline"
              className="min-h-12 px-4 rounded-full border-2"
              style={{ borderColor: PRIMARY, color: PRIMARY }}
              data-testid="scan-qr-btn"
            >
              <Camera className="w-4 h-4 mr-1.5" />
              Scan QR
            </Button>
            <Button
              onClick={() => validate()}
              disabled={!code.trim() || busy}
              className="min-h-12 px-8 rounded-full"
              style={{ background: PRIMARY, color: '#fff' }}
              data-testid="validate-btn"
            >
              {busy && !validation ? 'Validating…' : 'Validate'}
            </Button>
          </div>
        </div>

        <VoucherQrScanner
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScan={handleScan}
        />

        {validation && !validation.ok && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2"
               data-testid="validation-error">
            <XCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-sm text-rose-800">{validation.detail}</div>
          </div>
        )}

        {validation?.ok && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-4"
               data-testid="validation-ok">
            <div className="flex items-center gap-2 font-medium text-emerald-800">
              <CheckCircle2 className="w-5 h-5" />Voucher valid — confirm redemption
            </div>

            <MemberVoucherPanel v={validation} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-emerald-200 pt-3">
              <Field label="Bill Reference" testid="bill-ref">
                <Input value={bill.bill_ref}
                       onChange={(e) => setBill({ ...bill, bill_ref: e.target.value })}
                       className="min-h-11" />
              </Field>
              <Field label="Bill Amount (₹)" testid="bill-amount">
                <Input type="number" inputMode="decimal" value={bill.bill_amount}
                       onChange={(e) => setBill({ ...bill, bill_amount: e.target.value })}
                       className="min-h-11" />
              </Field>
              <Field label="Discount Given (₹)" testid="discount-amount">
                <Input type="number" inputMode="decimal" value={bill.discount_amount}
                       onChange={(e) => setBill({ ...bill, discount_amount: e.target.value })}
                       className="min-h-11" />
              </Field>
              <Field label="Notes">
                <Input value={bill.notes} onChange={(e) => setBill({ ...bill, notes: e.target.value })}
                       className="min-h-11" />
              </Field>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button onClick={redeem} disabled={busy}
                      className="min-h-12 flex-1 rounded-full"
                      style={{ background: PRIMARY, color: '#fff' }}
                      data-testid="redeem-btn">
                {busy ? 'Redeeming…' : 'Confirm Redemption'}
              </Button>
              <Button variant="outline" onClick={reset} className="min-h-12 rounded-full">Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ==================== EDC (20%) PANEL ==================== */

function EdcRedemptionPanel({ API, headers, outletId, onDone }) {
  const [identifier, setIdentifier] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [bill, setBill] = useState({ bill_ref: '', notes: '' });
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const idRef = useRef(null);

  const reset = () => {
    setIdentifier(''); setBillAmount('');
    setBill({ bill_ref: '', notes: '' });
    setValidation(null);
    idRef.current?.focus();
  };

  const validate = async () => {
    if (!identifier.trim()) return;
    setBusy(true); setValidation(null);
    try {
      const res = await axios.post(`${API}/manager/loyalty/edc/validate`, {
        identifier: identifier.trim(),
        outlet_id: outletId || undefined,
        bill_amount: billAmount === '' ? null : Number(billAmount),
      }, { headers: headers() });
      setValidation({ ok: true, ...res.data });
    } catch (e) {
      setValidation({ ok: false, detail: e?.response?.data?.detail || 'Validation failed' });
    } finally { setBusy(false); }
  };

  const redeem = async () => {
    if (!validation?.ok) return;
    if (billAmount === '' || Number(billAmount) <= 0) {
      toast.error('Enter a bill amount greater than zero'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/manager/loyalty/edc/redeem`, {
        identifier: identifier.trim(),
        outlet_id: outletId || undefined,
        bill_amount: Number(billAmount),
        bill_ref: bill.bill_ref || undefined,
        notes: bill.notes || undefined,
        presence_confirmed: true,
      }, { headers: headers() });
      toast.success('20% discount applied');
      reset();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Redeem failed');
    } finally { setBusy(false); }
  };

  const previewDiscount = billAmount === '' ? null
    : Math.round(Number(billAmount) * 20) / 100;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ color: PRIMARY }}>
          <Percent className="w-4 h-4" />Membership Card 20% Discount
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Membership Card # or Registered Mobile
          </Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              ref={idRef}
              placeholder="e.g. 4521-8823-1102 or 9876543210"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && validate()}
              className="min-h-12 text-lg tracking-wide flex-1"
              autoFocus
              data-testid="edc-identifier-input"
            />
            <Button
              onClick={validate}
              disabled={!identifier.trim() || busy}
              className="min-h-12 px-8 rounded-full"
              style={{ background: PRIMARY, color: '#fff' }}
              data-testid="edc-validate-btn"
            >
              {busy && !validation ? 'Looking up…' : 'Look Up Member'}
            </Button>
          </div>
        </div>

        {validation && !validation.ok && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2"
               data-testid="edc-error">
            <XCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-sm text-rose-800">{validation.detail}</div>
          </div>
        )}

        {validation?.ok && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-4"
               data-testid="edc-validation-ok">
            <div className="flex items-center gap-2 font-medium text-emerald-800">
              <CheckCircle2 className="w-5 h-5" />Member verified
            </div>

            <MemberEdcPanel v={validation} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-emerald-200 pt-3">
              <Field label="Bill Amount (₹)" testid="edc-bill-amount">
                <Input type="number" inputMode="decimal" value={billAmount}
                       onChange={(e) => setBillAmount(e.target.value)}
                       placeholder="0.00" className="min-h-11" />
              </Field>
              <Field label="Auto Discount (20%)">
                <div className="min-h-11 flex items-center px-3 rounded-md bg-white border font-medium"
                     style={{ color: PRIMARY }} data-testid="edc-preview-discount">
                  {previewDiscount == null ? '—' : INR(previewDiscount)}
                </div>
              </Field>
              <Field label="Bill Reference">
                <Input value={bill.bill_ref}
                       onChange={(e) => setBill({ ...bill, bill_ref: e.target.value })}
                       className="min-h-11" data-testid="edc-bill-ref" />
              </Field>
              <Field label="Notes">
                <Input value={bill.notes} onChange={(e) => setBill({ ...bill, notes: e.target.value })}
                       className="min-h-11" />
              </Field>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button onClick={redeem} disabled={busy}
                      className="min-h-12 flex-1 rounded-full"
                      style={{ background: PRIMARY, color: '#fff' }}
                      data-testid="edc-redeem-btn">
                {busy ? 'Applying…' : 'Confirm 20% Discount'}
              </Button>
              <Button variant="outline" onClick={reset} className="min-h-12 rounded-full">Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ==================== SHARED SUB-COMPONENTS ==================== */

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs font-medium text-gray-600 uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function InfoRow({ label, children, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="text-xs text-gray-500 flex items-center gap-1.5 shrink-0">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-sm font-medium text-right text-gray-900 break-all">
        {children}
      </div>
    </div>
  );
}

function MemberVoucherPanel({ v }) {
  const voucher = v.voucher || {};
  const member = v.member || {};
  const card = v.card || {};
  const outlet = v.outlet || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1 bg-white/60 rounded-md p-3 border border-emerald-100">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Member</div>
        <InfoRow label="Name" icon={User}>{member.name || '—'}</InfoRow>
        <InfoRow label="Phone" icon={Phone}>{member.phone || '—'}</InfoRow>
        <InfoRow label="Member ID"><span className="font-mono text-xs">{member.member_id_public || '—'}</span></InfoRow>
        <InfoRow label="Tier">
          <Badge variant="outline" className="capitalize">{card.tier || 'standard'}</Badge>
        </InfoRow>
        <InfoRow label="Card #" icon={CreditCard}>
          <span className="font-mono text-xs">{card.card_number || '—'}</span>
        </InfoRow>
      </div>
      <div className="space-y-1 bg-white/60 rounded-md p-3 border border-emerald-100">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Voucher</div>
        <InfoRow label="Code"><span className="font-mono">{voucher.voucher_code}</span></InfoRow>
        <InfoRow label="Type">{voucher.voucher_type_name}</InfoRow>
        <InfoRow label="Benefit">
          <span className="inline-flex items-center gap-1">
            <Badge variant="outline">{voucher.benefit_type}</Badge>
            {voucher.benefit_value != null && (
              <span className="text-gray-700">{voucher.benefit_value}</span>
            )}
          </span>
        </InfoRow>
        <InfoRow label="Expires">{String(voucher.valid_until || '').slice(0, 10) || '—'}</InfoRow>
        <InfoRow label="Applicable Outlet">{outlet.name || '—'}</InfoRow>
      </div>
    </div>
  );
}

function MemberEdcPanel({ v }) {
  const member = v.member || {};
  const card = v.card || {};
  const outlet = v.outlet || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1 bg-white/60 rounded-md p-3 border border-emerald-100">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Member</div>
        <InfoRow label="Name" icon={User}>{member.name || '—'}</InfoRow>
        <InfoRow label="Phone" icon={Phone}>{member.phone || '—'}</InfoRow>
        <InfoRow label="Member ID"><span className="font-mono text-xs">{member.member_id_public || '—'}</span></InfoRow>
      </div>
      <div className="space-y-1 bg-white/60 rounded-md p-3 border border-emerald-100">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Card</div>
        <InfoRow label="Number" icon={CreditCard}><span className="font-mono text-xs">{card.card_number || '—'}</span></InfoRow>
        <InfoRow label="Tier">
          <Badge variant="outline" className="capitalize">{card.tier || 'standard'}</Badge>
        </InfoRow>
        <InfoRow label="Expires">{String(card.expires_at || '').slice(0, 10) || '—'}</InfoRow>
        <InfoRow label="Applicable Outlet">{outlet.name || '—'}</InfoRow>
        <InfoRow label="Discount">
          <span className="font-semibold" style={{ color: PRIMARY }}>20% (fixed)</span>
        </InfoRow>
      </div>
    </div>
  );
}

function RecentList({ title, testid, rows, onRefresh, empty, isEdc = false }) {
  return (
    <Card data-testid={testid}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
        <Button size="sm" variant="ghost" onClick={onRefresh}>
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="p-6 text-xs text-center text-gray-400">{empty}</div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Time</th>
                  {isEdc
                    ? <><th className="text-left p-2">Card</th><th className="text-right p-2">Bill</th><th className="text-right p-2">−20%</th></>
                    : <><th className="text-left p-2">Voucher</th><th className="text-right p-2">Bill</th></>}
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="border-b last:border-b-0" data-testid={`recent-${e.id}`}>
                    <td className="p-2 whitespace-nowrap">{String(e.redeemed_at || '').slice(11, 19)}</td>
                    {isEdc ? (
                      <>
                        <td className="p-2 font-mono">{(e.card_number || '').slice(-6) || '—'}</td>
                        <td className="p-2 text-right">{INR(e.bill_amount)}</td>
                        <td className="p-2 text-right font-medium" style={{ color: PRIMARY }}>
                          {INR(e.discount_value)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-2 font-mono">{e.voucher_code}</td>
                        <td className="p-2 text-right">{INR(e.bill_amount)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
